import { useMemo, useState } from "react";
import { X, ClipboardPaste, FileSpreadsheet, Wifi, ArrowRight, ArrowLeft, Check, AlertTriangle, SkipForward } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { IMPORT } from "@/constants/testIds";

const PLACEHOLDER = `1\tFRANCIS KUSUNAN\t0.00\t0.00\t0.00\t0\t0
2\tRAPHAEL OPNAI\t78.09\t16.50\t11.50\t0\t0
3\tMANUP PASE\t53.97\t4.00\t10.50\t0\t0`;

const STEP_INPUT = "input";
const STEP_RECONCILE = "reconcile";

export default function ImportHoursModal({ open, onClose, onApplied, periodId }) {
    const [tab, setTab] = useState("paste");
    const [text, setText] = useState("");
    const [step, setStep] = useState(STEP_INPUT);
    const [busy, setBusy] = useState(false);
    // preview result
    const [matched, setMatched] = useState([]);        // [{source_name, employee_id, employee_name, hours}]
    const [unmatched, setUnmatched] = useState([]);    // [{source_name, hours}]
    const [employeeList, setEmployeeList] = useState([]); // [{id, name}]
    // per-unmatched-row decisions
    const [decisions, setDecisions] = useState({});    // { source_name: {mode: "map"|"skip", employee_id?: string} }

    const parsedRows = useMemo(() => {
        if (!text.trim()) return 0;
        return text.split("\n").map((l) => l.trim()).filter(Boolean).length;
    }, [text]);

    if (!open) return null;

    const reset = () => {
        setStep(STEP_INPUT);
        setMatched([]);
        setUnmatched([]);
        setEmployeeList([]);
        setDecisions({});
    };

    const handleClose = () => {
        reset();
        setText("");
        onClose?.();
    };

    const onCsvFile = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const t = await file.text();
        setText(t);
        setTab("paste");
    };

    const runPreview = async () => {
        setBusy(true);
        try {
            const { data } = await api.post(`/pay-periods/${periodId}/import-hours/preview`, { text });
            setMatched(data.matched || []);
            setUnmatched(data.unmatched || []);
            setEmployeeList(data.employees || []);
            // pre-set every unmatched row to "skip" so Confirm can only be enabled after a real decision
            const d = {};
            for (const u of (data.unmatched || [])) {
                d[u.source_name] = { mode: null };
            }
            setDecisions(d);
            setStep(STEP_RECONCILE);
        } catch (ex) {
            toast.error(formatApiError(ex?.response?.data?.detail) || "Could not read the paste");
        } finally {
            setBusy(false);
        }
    };

    const setDecision = (name, patch) => {
        setDecisions((prev) => ({ ...prev, [name]: { ...(prev[name] || {}), ...patch } }));
    };

    const allResolved = unmatched.every((u) => {
        const d = decisions[u.source_name];
        if (!d) return false;
        if (d.mode === "skip") return true;
        if (d.mode === "map") return !!d.employee_id;
        return false;
    });

    const willImport = matched.length + unmatched.filter((u) => decisions[u.source_name]?.mode === "map").length;
    const willSkip = unmatched.filter((u) => decisions[u.source_name]?.mode === "skip").length;

    const confirmApply = async () => {
        if (!allResolved) return;
        setBusy(true);
        const mappings = unmatched
            .filter((u) => decisions[u.source_name]?.mode === "map")
            .map((u) => ({ source_name: u.source_name, employee_id: decisions[u.source_name].employee_id }));
        const skips = unmatched
            .filter((u) => decisions[u.source_name]?.mode === "skip")
            .map((u) => u.source_name);
        try {
            const { data } = await api.post(`/pay-periods/${periodId}/import-hours`, { text, mappings, skips });
            const parts = [];
            if (data.updated_lines) parts.push(`✓ ${data.updated_lines} lines updated`);
            if (skips.length) parts.push(`${skips.length} skipped`);
            if (data.unmatched?.length) parts.push(`${data.unmatched.length} still unmatched`);
            const msg = parts.join(" · ") || "Import complete";
            if (data.unmatched?.length) toast.warning(msg, { duration: 8000 });
            else toast.success(msg);
            reset();
            setText("");
            onApplied?.(data);
        } catch (ex) {
            toast.error(formatApiError(ex?.response?.data?.detail) || "Import failed");
        } finally {
            setBusy(false);
        }
    };

    const tabs = [
        { id: "bio", icon: Wifi, label: "Connect biometric", disabled: true, note: "v1.2" },
        { id: "csv", icon: FileSpreadsheet, label: "Upload CSV" },
        { id: "paste", icon: ClipboardPaste, label: "Paste from Excel" },
    ];

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center" role="dialog" aria-modal="true">
            <div className="absolute inset-0 bg-black/50" onClick={handleClose} />
            <div data-testid={IMPORT.modal} className="relative bg-white border border-neutral-200 shadow-2xl w-full max-w-[820px] rounded-[3px]">
                <div className="px-6 py-5 border-b border-neutral-200 flex items-start justify-between">
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.24em] text-neutral-500 font-display">
                            Import hours {step === STEP_RECONCILE && "· Step 2 of 2 · Review matches"}
                        </div>
                        <div className="font-display font-bold text-2xl text-[color:var(--kp-charcoal)] mt-0.5">
                            {step === STEP_INPUT ? "From Santus biometric summary" : "Reconcile unmatched names"}
                        </div>
                    </div>
                    <button data-testid={IMPORT.closeBtn} onClick={handleClose} className="p-1 text-neutral-500 hover:text-black">
                        <X size={16} />
                    </button>
                </div>

                {step === STEP_INPUT ? (
                    <>
                        {/* Tabs */}
                        <div className="px-6 pt-4 flex gap-2">
                            {tabs.map((t) => (
                                <button
                                    key={t.id}
                                    data-testid={t.id === "paste" ? IMPORT.tabPaste : t.id === "csv" ? IMPORT.tabCsv : IMPORT.tabBio}
                                    disabled={t.disabled}
                                    onClick={() => !t.disabled && setTab(t.id)}
                                    title={t.disabled ? "Coming in v1.2 — biometric API integration" : undefined}
                                    className={`inline-flex items-center gap-2 px-3 py-2 text-[11px] font-display font-semibold uppercase tracking-[0.12em] border rounded-[3px] transition-colors ${
                                        t.disabled
                                            ? "border-neutral-200 text-neutral-300 cursor-not-allowed"
                                            : tab === t.id
                                            ? "border-[color:var(--kp-red)] text-[color:var(--kp-red)] bg-red-50"
                                            : "border-neutral-300 text-neutral-600 hover:border-neutral-400"
                                    }`}
                                >
                                    <t.icon size={13} />
                                    {t.label}
                                    {t.note && <span className="ml-1 text-[9px] opacity-70">· {t.note}</span>}
                                </button>
                            ))}
                        </div>

                        {/* Body */}
                        <div className="px-6 py-5">
                            {tab === "csv" ? (
                                <label className="block border-2 border-dashed border-neutral-300 rounded-[3px] p-8 text-center cursor-pointer hover:border-[color:var(--kp-red)]">
                                    <input data-testid={IMPORT.csvInput} type="file" accept=".csv,.tsv,text/csv,text/plain" onChange={onCsvFile} className="hidden" />
                                    <div className="text-sm text-neutral-600">Drop a CSV here or click to browse.</div>
                                    <div className="text-xs text-neutral-500 mt-1">
                                        Columns: Name, Regular, ×1.5, ×2.0, Sick, P/H
                                    </div>
                                </label>
                            ) : (
                                <div>
                                    <div className="text-[11px] uppercase tracking-[0.14em] text-neutral-500 font-display mb-1">
                                        Paste from Excel
                                    </div>
                                    <textarea
                                        data-testid={IMPORT.textarea}
                                        rows={9}
                                        value={text}
                                        onChange={(e) => setText(e.target.value)}
                                        placeholder={PLACEHOLDER}
                                        className="kp-focus w-full border border-neutral-300 px-3 py-2 text-[13px] font-mono-money bg-white rounded-[3px]"
                                        style={{ whiteSpace: "pre" }}
                                    />
                                    <div className="text-xs text-neutral-500 mt-2">
                                        Column order: <span className="font-mono-money">Name · Regular · ×1.5 · ×2.0 · Sick · P/H</span>. Tab, comma, or 2+ spaces all work. Numeric row-index is skipped.
                                    </div>
                                    <div data-testid={IMPORT.matchSummary} className="mt-3 text-[12px] text-neutral-600">
                                        {parsedRows > 0 ? (
                                            <span>{parsedRows} row(s) detected — click <strong>Review matches</strong> to see who lines up.</span>
                                        ) : (
                                            <span className="text-neutral-400">Waiting for paste…</span>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="px-6 py-4 border-t border-neutral-200 flex justify-end gap-3">
                            <button onClick={handleClose} className="px-4 py-2 text-[12px] font-display font-semibold uppercase tracking-[0.12em] text-neutral-600">
                                Cancel
                            </button>
                            <button
                                data-testid={IMPORT.continueBtn}
                                onClick={runPreview}
                                disabled={!text.trim() || busy}
                                className="inline-flex items-center gap-2 px-5 py-2 text-[12px] font-display font-semibold uppercase tracking-[0.12em] text-white rounded-[3px] disabled:opacity-40"
                                style={{ background: "var(--kp-red)" }}
                            >
                                {busy ? "Reading…" : "Review matches"} <ArrowRight size={14} />
                            </button>
                        </div>
                    </>
                ) : (
                    <>
                        {/* Reconcile step */}
                        <div data-testid={IMPORT.reconcilePanel} className="px-6 py-5 max-h-[520px] overflow-y-auto">
                            <div className="grid grid-cols-2 gap-6">
                                {/* Matched column */}
                                <div>
                                    <div className="flex items-center gap-2 mb-3">
                                        <Check size={14} style={{ color: "var(--kp-success)" }} />
                                        <div className="text-[11px] uppercase tracking-[0.14em] text-neutral-600 font-display font-semibold">
                                            Matched · {matched.length}
                                        </div>
                                    </div>
                                    {matched.length === 0 ? (
                                        <div className="text-xs text-neutral-400 italic border border-dashed border-neutral-200 rounded-[3px] p-4">
                                            Nothing matched automatically.
                                        </div>
                                    ) : (
                                        <ul className="space-y-1.5">
                                            {matched.map((m, i) => (
                                                <li
                                                    key={m.source_name + i}
                                                    data-testid={IMPORT.matchedRow(i)}
                                                    className="text-[12px] border border-neutral-200 rounded-[3px] px-3 py-2 bg-white"
                                                >
                                                    <div className="text-[color:var(--kp-charcoal)] font-medium">{m.employee_name}</div>
                                                    <div className="text-[11px] text-neutral-500">
                                                        from CSV: <span className="font-mono-money">{m.source_name}</span>
                                                    </div>
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </div>

                                {/* Unmatched column */}
                                <div>
                                    <div className="flex items-center gap-2 mb-3">
                                        <AlertTriangle size={14} style={{ color: "var(--kp-warn)" }} />
                                        <div className="text-[11px] uppercase tracking-[0.14em] text-neutral-600 font-display font-semibold">
                                            Needs a decision · {unmatched.length}
                                        </div>
                                    </div>
                                    {unmatched.length === 0 ? (
                                        <div className="text-xs text-neutral-400 italic border border-dashed border-neutral-200 rounded-[3px] p-4">
                                            No unmatched rows. You&rsquo;re good to import.
                                        </div>
                                    ) : (
                                        <ul className="space-y-3">
                                            {unmatched.map((u, i) => {
                                                const dec = decisions[u.source_name] || { mode: null };
                                                return (
                                                    <li
                                                        key={u.source_name + i}
                                                        data-testid={IMPORT.unmatchedRow(i)}
                                                        className={`border rounded-[3px] px-3 py-2.5 ${
                                                            dec.mode === "skip"
                                                                ? "border-neutral-300 bg-neutral-50 opacity-70"
                                                                : dec.mode === "map" && dec.employee_id
                                                                ? "border-[color:var(--kp-success)] bg-green-50"
                                                                : "border-orange-300 bg-orange-50"
                                                        }`}
                                                    >
                                                        <div className="text-[13px] text-[color:var(--kp-charcoal)] font-medium font-mono-money">
                                                            {u.source_name}
                                                        </div>
                                                        <div className="text-[10px] text-neutral-500 mt-0.5">
                                                            Regular {u.hours?.regular ?? 0} · ×1.5 {u.hours?.time_and_half ?? 0} · ×2 {u.hours?.double ?? 0}
                                                        </div>
                                                        <select
                                                            data-testid={IMPORT.unmatchedSelect(i)}
                                                            disabled={dec.mode === "skip"}
                                                            value={dec.mode === "map" ? (dec.employee_id || "") : ""}
                                                            onChange={(e) =>
                                                                setDecision(u.source_name, { mode: "map", employee_id: e.target.value })
                                                            }
                                                            className="kp-focus mt-2 w-full text-[12px] border border-neutral-300 bg-white px-2 py-1.5 rounded-[2px]"
                                                        >
                                                            <option value="">— Map to employee…</option>
                                                            {employeeList.map((e) => (
                                                                <option key={e.id} value={e.id}>{e.name}</option>
                                                            ))}
                                                        </select>
                                                        <label className="mt-2 flex items-center gap-2 text-[11px] text-neutral-600 cursor-pointer select-none">
                                                            <input
                                                                data-testid={IMPORT.unmatchedSkip(i)}
                                                                type="checkbox"
                                                                checked={dec.mode === "skip"}
                                                                onChange={(e) => setDecision(u.source_name, { mode: e.target.checked ? "skip" : null, employee_id: undefined })}
                                                            />
                                                            <SkipForward size={12} className="text-neutral-400" />
                                                            Skip this row
                                                        </label>
                                                    </li>
                                                );
                                            })}
                                        </ul>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className="px-6 py-4 border-t border-neutral-200 flex items-center justify-between">
                            <button
                                data-testid={IMPORT.reconcileBack}
                                onClick={() => setStep(STEP_INPUT)}
                                className="inline-flex items-center gap-2 px-3 py-2 text-[12px] font-display font-semibold uppercase tracking-[0.12em] text-neutral-600 hover:text-black"
                            >
                                <ArrowLeft size={14} /> Back
                            </button>
                            <div className="flex items-center gap-4">
                                <div data-testid={IMPORT.reconcileSummary} className="text-[12px] text-neutral-600">
                                    Importing <strong className="text-[color:var(--kp-charcoal)]">{willImport}</strong> rows, skipping <strong className="text-[color:var(--kp-charcoal)]">{willSkip}</strong>
                                </div>
                                <button
                                    data-testid={IMPORT.reconcileConfirmBtn}
                                    onClick={confirmApply}
                                    disabled={!allResolved || busy}
                                    title={allResolved ? "Confirm import" : "Choose Map or Skip for every unmatched row"}
                                    className="inline-flex items-center gap-2 px-5 py-2 text-[12px] font-display font-semibold uppercase tracking-[0.12em] text-white rounded-[3px] disabled:opacity-40 disabled:cursor-not-allowed"
                                    style={{ background: "var(--kp-red)" }}
                                >
                                    {busy ? "Importing…" : "Confirm import"} <Check size={14} />
                                </button>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}
